<script setup lang="ts">
    import { computed, inject, onMounted, reactive, watch, ref } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import List from '@shared/components/List/List.vue';
    import BSFormSearch from '@shared/components/BSFormSearch.vue';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { OAuthProviderAuditLogAccess } from './OAuthProviderAuditLog.access';
    import Loading from '@shared/components/Loading.vue';
    import JsonViewer from '@shared/components/JsonViewer.vue';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const auditLogAccess: OAuthProviderAuditLogAccess = injectorAccess.injector.get(OAuthProviderAuditLogAccess);
    const route = useRoute();
    const router = useRouter();

    const expandedLogs = ref<Set<string>>(new Set());

    const state = reactive<{
        selectedLogType: string;
    }>({
        selectedLogType: 'all',
    });

    onMounted(async () => {
        if (route.params.oauthProviderId) {
            await auditLogAccess.loadProvider(route.params.oauthProviderId as string);
            await auditLogAccess.loadAuditLogs(route.params.oauthProviderId as string);
            focusSearchInput();
        } else {
            router.push({ name: 'OAuthProviders' });
        }
    });

    function focusSearchInput(): void {
        setTimeout(() => {
            document.getElementById('audit-log-search')?.focus();
        }, 50);
    }

    watch(
        () => auditLogAccess.state.freeTextFilter,
        (newVal) => {
            auditLogAccess.loadAuditLogs(route.params.oauthProviderId as string, true);
        }
    );

    watch(
        () => state.selectedLogType,
        (newVal) => {
            updateLogTypeFilter(newVal);
        }
    );

    function updateLogTypeFilter(filter: string): void {
        const logType = getLogTypeFromFilter(filter);
        auditLogAccess.setLogType(logType);
        auditLogAccess.loadAuditLogs(route.params.oauthProviderId as string, true);
    }

    function getLogTypeFromFilter(filter: string): number | null {
        switch (filter) {
            case 'login':
                return 3;
            case 'loginFailed':
                return 4;
            case 'userCreated':
                return 10;
            case 'all':
            default:
                return null;
        }
    }

    function toggleOptions() {
        return [
            { display: 'All', key: 'all' },
            { display: 'Login', key: 'login' },
            { display: 'Login Failed', key: 'loginFailed' },
            { display: 'User Created', key: 'userCreated' },
        ];
    }

    function refreshLogs(): void {
        auditLogAccess.loadAuditLogs(route.params.oauthProviderId as string, true);
    }

    function toggleLogExpansion(logId: string): void {
        if (expandedLogs.value.has(logId)) {
            expandedLogs.value.delete(logId);
        } else {
            expandedLogs.value.add(logId);
        }
    }

    function isLogExpanded(logId: string): boolean {
        return expandedLogs.value.has(logId);
    }

    function parseJsonData(data: string): any {
        if (!data) return data;

        try {
            // Attempt to parse as JSON
            return JSON.parse(data);
        } catch (e) {
            // If not valid JSON, return as is
            return data;
        }
    }
</script>

<template>
    <div>
        <div v-if="auditLogAccess.state.provider">
            <router-link :to="{ name: 'OAuthProviders' }">
                <span class="glyphicon glyphicon-chevron-left c-light" aria-hidden="true"></span>
                View all
            </router-link>

            <h4>
                Audit Logs for: {{ auditLogAccess.state.provider.ProviderName }}
                <router-link :to="{ name: 'EditOAuthProvider', params: { oauthProviderId: route.params.oauthProviderId } }" class="btn btn-primary btn-sm">
                    Edit Provider
                </router-link>
            </h4>

            <List
                displayAs="table"
                itemId="UserLogId"
                title="OAuth Provider Audit Logs"
                :totalItemCount="auditLogAccess.state.totalCount"
                :perPage="auditLogAccess.state.pageSize"
                :items="auditLogAccess.state.auditLogs"
                :tableHeaders="['Date/Time', 'Type', 'User', 'Details']"
                :loading="auditLogAccess.state.loading"
                :externalSearchInput="true"
                :serverSideFilter="true"
                :selectedToggle="state.selectedLogType"
                :toggleOptions="toggleOptions()"
                @update:selectedToggle="state.selectedLogType = $event"
                :orderOptions="[
                    { key: 'DateTime desc', display: 'Date (newest first)' },
                    { key: 'DateTime asc', display: 'Date (oldest first)' },
                    { key: 'UserName', display: 'User Name' },
                ]"
                @orderChange="
                    auditLogAccess.setOrderBy($event);
                    auditLogAccess.loadAuditLogs(route.params.oauthProviderId as string, true);
                "
                @pageChange="
                    auditLogAccess.setPage($event);
                    auditLogAccess.loadAuditLogs(route.params.oauthProviderId as string);
                ">
                <template #search>
                    <BSFormSearch
                        v-model="auditLogAccess.state.freeTextFilter"
                        class="search-input-width"
                        inputId="audit-log-search"
                        :disabled="false"
                        @reset="() => (auditLogAccess.state.freeTextFilter = '')"></BSFormSearch>
                </template>
                <template #toolbar>
                    <button class="btn btn-subtle" @click="refreshLogs">Refresh</button>
                </template>
                <template #item="{ item: log }">
                    <td class="datetime-column">{{ formatDateTime(log.DateTime) }}</td>
                    <td class="type-column">{{ auditLogAccess.getLogTypeName(log.Type) }}</td>
                    <td class="user-column">
                        {{ log.UserName }}
                        <br />
                        <small>{{ log.UserEmail }}</small>
                    </td>
                    <td class="details-column">
                        <div v-if="log.Data" class="log-data-container">
                            <div class="log-data-header" @click="toggleLogExpansion(log.UserLogId)">
                                <i class="fa" :class="isLogExpanded(log.UserLogId) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                <span class="log-data-preview">
                                    {{ log.Data.length > 50 ? log.Data.substring(0, 50) + '...' : log.Data }}
                                </span>
                            </div>
                            <div v-if="isLogExpanded(log.UserLogId)" class="log-data-wrapper">
                                <pre class="raw-data">{{ log.Data }}</pre>
                            </div>
                        </div>
                        <span v-else>-</span>
                    </td>
                </template>
            </List>
        </div>
        <Loading v-else :show="auditLogAccess.state.loading" loadingStyle="progress-bar"></Loading>
    </div>
</template>

<style scoped>
    .log-data-wrapper {
        margin-top: 5px;
        max-height: 300px;
        overflow-y: auto;
    }

    .log-data-container {
        cursor: pointer;
    }

    .log-data-header {
        display: flex;
        align-items: center;
        background-color: #f5f5f5;
        padding: 5px;
        border-radius: 3px;
    }

    .log-data-header i {
        margin-right: 5px;
    }

    .log-data-preview {
        font-family: monospace;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .btn-sm {
        margin-left: 10px;
    }

    .datetime-column {
        width: 160px;
        min-width: 160px;
        max-width: 160px;
        white-space: nowrap;
    }

    .type-column {
        width: 120px;
        min-width: 120px;
        max-width: 120px;
        white-space: nowrap;
    }

    .user-column {
        width: 180px;
        min-width: 180px;
        max-width: 180px;
        word-break: break-word;
    }

    .details-column {
        min-width: 200px;
        width: auto;
        flex: 1;
        overflow-wrap: break-word;
        word-wrap: break-word;
    }

    .json-preview-key {
        color: #881391;
        font-weight: bold;
    }

    .json-preview-value {
        color: #268bd2;
    }

    .json-preview-separator {
        color: #666;
    }

    .truncated {
        max-width: 150px;
        display: inline-block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: bottom;
    }

    .raw-data {
        font-family: monospace;
        font-size: 14px;
        line-height: 1.4;
        color: #333;
        background-color: #f8f8f8;
        border-radius: 3px;
        padding: 10px;
        margin: 0;
        white-space: pre-wrap;
        word-break: break-word;
    }
</style>
